import { validateEmail } from '@modules/common/utils';
import { EmailRounded } from '@mui/icons-material';
import { Autocomplete, Box, Stack, TextField, Typography } from '@mui/material';
import { SyntheticEvent, useState } from 'react';
import { IsLoading } from 'v2/core/repository/edge-server/types';
import { useListUsers } from 'v2/core/services/listUsers';
import { useGetUser } from 'v2/core/services/user';

interface EmailOutboundInputProps {
  selectedEmails: string[];
  onSelectedEmailsChange: (
    emails: string[],
    hasInvalidEmails?: boolean,
  ) => void;
  errorMessage?: string;
  setErrorMessage?: (message: string) => void;
}

const EmailOutboundInput = ({
  selectedEmails,
  onSelectedEmailsChange,
  errorMessage,
  setErrorMessage,
}: EmailOutboundInputProps) => {
  const {
    tenant: { domains: allowedDomains },
  } = useGetUser();

  const [inputValue, setInputValue] = useState('');

  const { data: usersData, status } = useListUsers();

  const userEmails = usersData?.users?.map((user) => user.email) || [];

  const hasInvalidEmails = (emails: string[]) =>
    !!emails.find((email) => !validateEmail(email, allowedDomains).isValid);

  const onEmailsChange = (_: SyntheticEvent | null, emails: string[]) => {
    if (errorMessage !== '') {
      setErrorMessage?.('');
    }
    if (inputValue !== '') {
      setInputValue('');
    }
    onSelectedEmailsChange(emails, hasInvalidEmails(emails));
  };

  const errorText = hasInvalidEmails(selectedEmails)
    ? 'One or more emails are invalid. Allowed domains are: ' +
      allowedDomains?.join(', ')
    : errorMessage;

  const isUsersLoading = IsLoading(status);

  return (
    <Stack spacing={2}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <EmailRounded fontSize="small" />
        <Typography variant="body1">Email</Typography>
      </Stack>
      <Autocomplete
        freeSolo
        multiple
        value={selectedEmails}
        isOptionEqualToValue={(option, value) => option === value}
        options={userEmails}
        loading={isUsersLoading}
        limitTags={6}
        getOptionLabel={(option) =>
          typeof option === 'string' ? option : option
        }
        disableCloseOnSelect
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            placeholder="Enter email IDs"
            error={!!errorText}
            helperText={errorText}
            id="email-error-text"
          />
        )}
        renderOption={(props, option) => (
          <Box {...props} component="li" style={{ padding: '16px' }}>
            {option}
          </Box>
        )}
        onChange={onEmailsChange}
        inputValue={inputValue}
        onInputChange={(_, newInputValue) => {
          setInputValue(newInputValue);
        }}
        onBlur={(e) => {
          if (!inputValue.trim()) {
            setInputValue('');
            return;
          }
          onEmailsChange(e, [...selectedEmails, inputValue]);
        }}
      />
    </Stack>
  );
};

export default EmailOutboundInput;
